<template>
  <div class="yardage-tab">
    <div class="tab-header">
      <el-button v-if="canEdit" type="primary" size="small" @click="openAddDialog">
        요척 추가
      </el-button>
    </div>

    <el-table :data="yardages" stripe size="small" border>
      <el-table-column type="expand">
        <template #default="{ row }">
          <div v-if="row.is_size_dependent && row.size_usages?.length" class="expand-content">
            <el-table :data="row.size_usages" size="small" border style="max-width: 400px; margin-left: 48px;">
              <el-table-column prop="size" label="사이즈" width="100" />
              <el-table-column prop="usage_per_unit" label="소요량" width="120" align="right" />
            </el-table>
          </div>
          <div v-else class="expand-content" style="margin-left: 48px; color: #909399;">
            사이즈별 차이 없음 (기본 소요량: {{ row.default_usage }})
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="material_type_display" label="구분" width="80" align="center">
        <template #default="{ row }">
          <el-tag :type="row.material_type === 'RAW' ? '' : 'warning'" size="small">
            {{ row.material_type_display }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="material_name" label="자재명" min-width="140" />
      <el-table-column prop="specification" label="규격" min-width="100" />
      <el-table-column prop="color" label="컬러" width="80" />
      <el-table-column prop="unit" label="단위" width="60" align="center" />
      <el-table-column label="사이즈별" width="80" align="center">
        <template #default="{ row }">
          <el-tag :type="row.is_size_dependent ? 'success' : 'info'" size="small">
            {{ row.is_size_dependent ? '있음' : '없음' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="기본소요량" width="100" align="right">
        <template #default="{ row }">{{ row.default_usage }}</template>
      </el-table-column>
      <el-table-column label="로스율(%)" width="90" align="right">
        <template #default="{ row }">{{ row.loss_rate }}%</template>
      </el-table-column>
      <el-table-column prop="marker_name" label="마카" width="100" />
      <el-table-column v-if="canEdit" label="작업" width="100" align="center">
        <template #default="{ row }">
          <el-button size="small" type="primary" link @click="openEditDialog(row)">수정</el-button>
          <el-button size="small" type="danger" link @click="deleteYardage(row)">삭제</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-empty v-if="yardages.length === 0" description="등록된 요척이 없습니다" />

    <!-- 추가/수정 다이얼로그 -->
    <el-dialog
      v-model="showDialog"
      :title="isEdit ? '요척 수정' : '요척 추가'"
      width="600px"
      destroy-on-close
    >
      <el-form :model="form" label-width="110px">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="자재구분" required>
              <el-select v-model="form.material_type" style="width: 100%">
                <el-option label="원자재" value="RAW" />
                <el-option label="부자재" value="ACC" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="연결 마카">
              <el-select v-model="form.marker" clearable placeholder="선택" style="width: 100%">
                <el-option
                  v-for="m in markers"
                  :key="m.id"
                  :label="m.marker_name"
                  :value="m.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="자재명" required>
          <el-input v-model="form.material_name" placeholder="자재명" />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="규격">
              <el-input v-model="form.specification" placeholder="규격" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="컬러">
              <el-input v-model="form.color" placeholder="컬러" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="단위">
              <el-select v-model="form.unit" style="width: 100%">
                <el-option label="YD" value="YD" />
                <el-option label="M" value="M" />
                <el-option label="EA" value="EA" />
                <el-option label="SET" value="SET" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="기본소요량">
              <el-input-number v-model="form.default_usage" :precision="4" :min="0" :step="0.01" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="로스율(%)">
              <el-input-number v-model="form.loss_rate" :precision="2" :min="0" :max="100" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 사이즈별 소요량 토글 -->
        <el-form-item label="사이즈별 차이">
          <el-switch v-model="form.is_size_dependent" active-text="있음" inactive-text="없음" />
        </el-form-item>

        <!-- 사이즈별 소요량 입력 -->
        <div v-if="form.is_size_dependent" class="size-usages-section">
          <div class="size-usages-header">
            <span style="font-weight: 600; font-size: 13px;">사이즈별 소요량</span>
            <el-button size="small" @click="addSizeRow">사이즈 추가</el-button>
          </div>
          <el-table :data="form.size_usages" size="small" border style="margin-top: 8px;">
            <el-table-column label="사이즈" width="140">
              <template #default="{ row }">
                <el-input v-model="row.size" size="small" placeholder="S, M, L..." />
              </template>
            </el-table-column>
            <el-table-column label="소요량" width="160">
              <template #default="{ row }">
                <el-input-number v-model="row.usage_per_unit" :precision="4" :min="0" :step="0.01" size="small" style="width: 100%" />
              </template>
            </el-table-column>
            <el-table-column label="" width="60">
              <template #default="{ $index }">
                <el-button size="small" type="danger" link @click="removeSizeRow($index)">삭제</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <el-form-item label="메모" style="margin-top: 12px;">
          <el-input v-model="form.memo" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showDialog = false">취소</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">
          {{ isEdit ? '수정' : '추가' }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'

const props = defineProps({
  designId: { type: [String, Number], required: true },
  yardages: { type: Array, default: () => [] },
  markers: { type: Array, default: () => [] },
  canEdit: { type: Boolean, default: false }
})

const emit = defineEmits(['updated'])

const showDialog = ref(false)
const isEdit = ref(false)
const editData = ref(null)
const submitting = ref(false)

const form = reactive({
  material_type: 'RAW',
  material_name: '',
  specification: '',
  color: '',
  unit: 'YD',
  is_size_dependent: true,
  default_usage: 0,
  loss_rate: 0,
  marker: null,
  memo: '',
  size_usages: []
})

const resetForm = () => {
  form.material_type = 'RAW'
  form.material_name = ''
  form.specification = ''
  form.color = ''
  form.unit = 'YD'
  form.is_size_dependent = true
  form.default_usage = 0
  form.loss_rate = 0
  form.marker = null
  form.memo = ''
  form.size_usages = [
    { size: 'S', usage_per_unit: 0 },
    { size: 'M', usage_per_unit: 0 },
    { size: 'L', usage_per_unit: 0 },
    { size: 'XL', usage_per_unit: 0 }
  ]
}

const openAddDialog = () => {
  isEdit.value = false
  editData.value = null
  resetForm()
  showDialog.value = true
}

const openEditDialog = (row) => {
  isEdit.value = true
  editData.value = row
  form.material_type = row.material_type
  form.material_name = row.material_name
  form.specification = row.specification || ''
  form.color = row.color || ''
  form.unit = row.unit
  form.is_size_dependent = row.is_size_dependent
  form.default_usage = Number(row.default_usage)
  form.loss_rate = Number(row.loss_rate)
  form.marker = row.marker || null
  form.memo = row.memo || ''
  form.size_usages = (row.size_usages || []).map(su => ({
    size: su.size,
    usage_per_unit: Number(su.usage_per_unit)
  }))
  if (form.size_usages.length === 0 && form.is_size_dependent) {
    form.size_usages = [{ size: '', usage_per_unit: 0 }]
  }
  showDialog.value = true
}

const addSizeRow = () => {
  form.size_usages.push({ size: '', usage_per_unit: 0 })
}

const removeSizeRow = (index) => {
  form.size_usages.splice(index, 1)
}

const submitForm = async () => {
  if (!form.material_name) {
    ElMessage.warning('자재명은 필수입니다.')
    return
  }

  submitting.value = true
  try {
    const payload = {
      material_type: form.material_type,
      material_name: form.material_name,
      specification: form.specification,
      color: form.color,
      unit: form.unit,
      is_size_dependent: form.is_size_dependent,
      default_usage: form.default_usage,
      loss_rate: form.loss_rate,
      marker: form.marker,
      memo: form.memo,
      size_usages: form.is_size_dependent
        ? form.size_usages.filter(su => su.size)
        : []
    }

    if (isEdit.value) {
      await axiosInstance.put(
        `designs/${props.designId}/yardages/${editData.value.id}/`,
        payload
      )
      ElMessage.success('요척이 수정되었습니다.')
    } else {
      await axiosInstance.post(
        `designs/${props.designId}/yardages/`,
        payload
      )
      ElMessage.success('요척이 추가되었습니다.')
    }

    showDialog.value = false
    emit('updated')
  } catch (error) {
    ElMessage.error('저장 중 오류가 발생했습니다.')
  } finally {
    submitting.value = false
  }
}

const deleteYardage = async (row) => {
  try {
    await ElMessageBox.confirm(`"${row.material_name}" 요척을 삭제하시겠습니까?`, '확인')
    await axiosInstance.delete(`designs/${props.designId}/yardages/${row.id}/`)
    ElMessage.success('요척이 삭제되었습니다.')
    emit('updated')
  } catch {
    // cancelled
  }
}
</script>

<style scoped>
.yardage-tab {
  padding: 20px;
}
.tab-header {
  margin-bottom: 15px;
}
.expand-content {
  padding: 10px 0;
}
.size-usages-section {
  background: #f5f7fa;
  padding: 12px;
  border-radius: 6px;
  margin-left: 110px;
}
.size-usages-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
